import React, { useEffect, useState } from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { useField } from '@unform/core';

type TVTextFieldProps = TextFieldProps & {
  name: string;
  calcular?: (value: number) => void;
  // Casas decimais do campo — default 4 (comportamento original). Produto
  // com GLP derivado de petróleo (qtdglp > 0) exige 10 casas no Preço
  // Tributado, ver DialogNfe.tsx.
  decimais?: number;
  // Quando true, a vírgula é digitada pelo usuário pra determinar onde
  // termina a parte inteira, em vez do mascaramento clássico (que trata
  // todo dígito digitado como se fosse casa decimal e empurra o resto pra
  // esquerda). Sem isso, um campo com decimais=10 exigiria digitar 10
  // dígitos só pra formar a parte decimal antes de conseguir escrever a
  // parte inteira — ver Preço Tributado em DialogNfe.tsx.
  digitarVirgula?: boolean;
};

export const VFieldMoneyNumber4dn: React.FC<TVTextFieldProps> = ({
  name,
  calcular,
  decimais = 4,
  digitarVirgula = false,
  ...rest
}) => {
  const {
    fieldName,
    registerField,
    defaultValue = 0,
    error,
    clearError,
  } = useField(name);

  function formatToBR(val: number | string): string {
    const num =
      typeof val === 'number'
        ? val
        : parseToNumber(val);

    return num.toLocaleString('pt-BR', {
      minimumFractionDigits: decimais,
      maximumFractionDigits: decimais,
    });
  }

  const [value, setValue] = useState<string>(() =>
    formatToBR(defaultValue)
  );

  useEffect(() => {
    registerField({
      name: fieldName,

      // 👉 valor REAL que vai para o formulário
      getValue: () => parseToNumber(value),

      // 👉 quando o valor vem de fora (setFieldValue)
      setValue: (_, newValue: number) => {
        setValue(formatToBR(newValue));
      },

      clearValue: () => setValue(formatToBR(0)),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldName, registerField, value, decimais]);

  /* ===============================
     Helpers
     =============================== */

  function parseToNumber(val: string): number {
    if (!val) return 0;

    return Number(
      val.replace(/\./g, '').replace(',', '.')
    ) || 0;
  }

  /* ===============================
     Change handler (DIGITAÇÃO)
     =============================== */
  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
  // remove tudo que não for número
  const digitsOnly = e.target.value.replace(/\D/g, '');

  const numberValue = Number(digitsOnly) / Math.pow(10, decimais);

  setValue(formatToBR(numberValue));
  calcular?.(numberValue);

  rest.onChange?.(e);
}

  /* ===============================
     Change handler (DIGITAÇÃO LIVRE — usuário escreve a vírgula)
     =============================== */
  function handleChangeLivre(e: React.ChangeEvent<HTMLInputElement>) {
    // mantém só dígitos e vírgula, digitados livremente
    let raw = e.target.value.replace(/[^\d,]/g, '');

    const primeiraVirgula = raw.indexOf(',');
    if (primeiraVirgula !== -1) {
      const parteInteira = raw.slice(0, primeiraVirgula).replace(/,/g, '');
      const parteDecimal = raw
        .slice(primeiraVirgula + 1)
        .replace(/,/g, '')
        .slice(0, decimais);
      raw = `${parteInteira},${parteDecimal}`;
    }

    setValue(raw);
    calcular?.(parseToNumber(raw));

    rest.onChange?.(e);
  }

  function handleBlurLivre(e: React.FocusEvent<HTMLInputElement>) {
    setValue(formatToBR(parseToNumber(value)));
    rest.onBlur?.(e);
  }

  return (
    <TextField
      {...rest}
      value={value}
      error={!!error}
      helperText={error}
      onChange={digitarVirgula ? handleChangeLivre : handleChange}
      onBlur={digitarVirgula ? handleBlurLivre : rest.onBlur}
      onKeyDown={(e) => {
        if (error) clearError();
        rest.onKeyDown?.(e);
      }}
    />
  );
};
